import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getPublicClubsApi } from "../api/match.api";

import Input from "../components/common/Input";
import Pagination from "../components/common/Pagination";
import Icon from "../components/common/Icon";

function getImageUrl(path) {
  if (!path) return null;

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:")
  ) {
    return path;
  }

  const base =
    process.env.REACT_APP_API_BASE_URL?.replace(/\/api\/v1\/?$/, "") ||
    window.location.origin;

  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

function getPlayerCount(club) {
  if (Array.isArray(club?.players)) {
    return club.players.length;
  }

  return (
    club?.playersCount ??
    club?.playerCount ??
    club?._count?.players ??
    club?.statistics?.players ??
    0
  );
}

function getAchievementCount(club) {
  if (Array.isArray(club?.achievements)) {
    return club.achievements.length;
  }

  return (
    club?.achievementsCount ??
    club?._count?.achievements ??
    club?.statistics?.achievements ??
    0
  );
}

function getManagerName(club) {
  const manager =
    club?.manager ||
    club?.clubManager ||
    null;

  if (!manager) return null;

  return (
    manager.fullName ||
    manager.name ||
    [manager.firstName, manager.lastName]
      .filter(Boolean)
      .join(" ") ||
    null
  );
}

function getLocation(club) {
  return (
    club?.city ||
    club?.province ||
    club?.address ||
    null
  );
}

function getInitial(name) {
  return name?.trim()?.charAt(0) || "⚽";
}

function ClubLogo({ club }) {
  const logo = getImageUrl(club?.logo);

  return (
    <div className="relative shrink-0">
      <div className="absolute inset-0 rounded-[1.75rem] bg-emerald-400/20 blur-xl transition duration-500 group-hover:scale-110 group-hover:bg-emerald-400/35" />

      <div className="relative grid h-20 w-20 place-items-center overflow-hidden rounded-[1.75rem] border border-white/80 bg-gradient-to-br from-white via-emerald-50 to-slate-100 shadow-xl ring-4 ring-white transition duration-300 group-hover:scale-105 sm:h-24 sm:w-24">
        {logo ? (
          <img
            src={logo}
            alt={club?.name || "باشگاه"}
            className="h-full w-full object-contain p-3"
            loading="lazy"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <span className="text-3xl font-black text-emerald-500 sm:text-4xl">
            {getInitial(club?.name)}
          </span>
        )}
      </div>
    </div>
  );
}

function ClubMetric({
  icon,
  value,
  label,
  tone = "emerald",
}) {
  const tones = {
    emerald:
      "bg-emerald-50 text-emerald-600",
    amber:
      "bg-amber-50 text-amber-600",
    blue:
      "bg-blue-50 text-blue-600",
  };

  return (
    <div className="flex min-w-[92px] flex-1 items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 p-3 transition duration-300 group-hover:border-slate-200 group-hover:bg-white">
      <div
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${tones[tone]}`}
      >
        <Icon
          name={icon}
          className="h-4 w-4"
        />
      </div>

      <div>
        <strong className="block text-base font-black text-slate-800">
          {Number(value || 0).toLocaleString("fa-IR")}
        </strong>

        <span className="text-[10px] font-medium text-slate-400">
          {label}
        </span>
      </div>
    </div>
  );
}

function ClubCard({ club, onClick }) {
  const playerCount = getPlayerCount(club);
  const achievementCount = getAchievementCount(club);
  const managerName = getManagerName(club);
  const location = getLocation(club);

  return (
    <article
      onClick={onClick}
      className="group relative cursor-pointer overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-2xl hover:shadow-emerald-900/[0.10]"
    >
      {/* Background glow */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-emerald-400/[0.05] blur-3xl transition duration-700 group-hover:scale-150 group-hover:bg-emerald-400/[0.10]" />

      <div className="pointer-events-none absolute -bottom-32 left-10 h-52 w-52 rounded-full bg-lime-400/[0.04] blur-3xl" />

      {/* Accent */}
      <div className="absolute inset-y-0 right-0 w-1 bg-gradient-to-b from-emerald-400 via-emerald-500 to-lime-400 opacity-0 transition duration-300 group-hover:opacity-100" />

      <div className="relative p-5 sm:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center">

          {/* Club identity */}
          <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-5">
            <ClubLogo club={club} />

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-xl font-black text-slate-800 transition group-hover:text-emerald-600 sm:text-2xl">
                  {club.name || "باشگاه بدون نام"}
                </h2>

                {club.status === "ACTIVE" && (
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[9px] font-black text-emerald-600">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                    فعال
                  </span>
                )}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                {club.shortName && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black text-slate-500">
                    {club.shortName}
                  </span>
                )}

                {location && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
                    <Icon
                      name="map-pin"
                      className="h-3.5 w-3.5 text-emerald-500"
                    />

                    <span className="max-w-[200px] truncate">
                      {location}
                    </span>
                  </span>
                )}
              </div>

              {club.description && (
                <p className="mt-3 line-clamp-2 max-w-2xl text-xs leading-6 text-slate-500 sm:text-sm">
                  {club.description}
                </p>
              )}
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:w-[330px]">
            <ClubMetric
              icon="users"
              value={playerCount}
              label="بازیکن"
              tone="emerald"
            />

            <ClubMetric
              icon="award"
              value={achievementCount}
              label="افتخار"
              tone="amber"
            />

            <div className="col-span-2 flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 p-3 sm:col-span-1 xl:hidden">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-blue-50 text-blue-600">
                <Icon
                  name="shield"
                  className="h-4 w-4"
                />
              </div>

              <div>
                <strong className="block text-xs font-black text-slate-700">
                  باشگاه عضو
                </strong>

                <span className="text-[10px] text-slate-400">
                  سامانه فوتبال
                </span>
              </div>
            </div>
          </div>

          {/* Action */}
          <div className="flex shrink-0 items-center justify-between gap-4 border-t border-slate-100 pt-4 xl:flex-col xl:justify-center xl:border-t-0 xl:border-r xl:border-slate-100 xl:pr-5 xl:pt-0">
            <div className="text-right xl:hidden">
              <p className="text-[10px] text-slate-400">
                اطلاعات کامل باشگاه
              </p>

              <p className="mt-1 text-xs font-black text-emerald-600">
                مشاهده پروفایل
              </p>
            </div>

            <div className="hidden text-center xl:block">
              <p className="text-[10px] text-slate-400">
                مشاهده
              </p>

              <p className="mt-1 text-xs font-black text-emerald-600">
                پروفایل باشگاه
              </p>
            </div>

            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[#0B1F17] text-white shadow-lg transition duration-300 group-hover:-translate-x-1 group-hover:bg-emerald-500 group-hover:shadow-emerald-500/25">
              <Icon
                name="arrowLeft"
                className="h-5 w-5"
              />
            </div>
          </div>
        </div>

        {/* Manager */}
        {managerName && (
          <div className="mt-5 flex items-center gap-3 border-t border-slate-100 pt-4">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 text-slate-400 transition group-hover:bg-emerald-50 group-hover:text-emerald-500">
              <Icon
                name="users"
                className="h-4 w-4"
              />
            </div>

            <div>
              <p className="text-[9px] font-medium text-slate-400">
                مدیر باشگاه
              </p>

              <p className="mt-0.5 text-xs font-black text-slate-700">
                {managerName}
              </p>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export default function Clubs() {
  const navigate = useNavigate();

  const [clubs, setClubs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const totalLabel = useMemo(() => {
    if (loading) return "...";

    return Number(
      pagination.total || 0
    ).toLocaleString("fa-IR");
  }, [loading, pagination.total]);

  useEffect(() => {
    let mounted = true;

    setLoading(true);

    getPublicClubsApi({
      search,
      page: pagination.page,
      limit: pagination.limit,
    })
      .then(({ data }) => {
        if (!mounted) return;

        const responseData =
          data?.data;

        const items = Array.isArray(responseData)
          ? responseData
          : responseData?.items ||
            responseData?.clubs ||
            [];

        setClubs(items);

        setPagination((prev) => ({
          ...prev,
          total:
            data?.meta?.total ??
            responseData?.total ??
            items.length ??
            0,
          totalPages:
            data?.meta?.totalPages ??
            responseData?.totalPages ??
            1,
        }));
      })
      .catch(() => {
        if (mounted) {
          setClubs([]);

          setPagination((prev) => ({
            ...prev,
            total: 0,
            totalPages: 1,
          }));
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [
    search,
    pagination.page,
    pagination.limit,
  ]);

  const handleSearch = (event) => {
    setSearch(event.target.value);

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const clearSearch = () => {
    setSearch("");

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePageChange = (page) => {
    if (
      page >= 1 &&
      page <= pagination.totalPages
    ) {
      setPagination((prev) => ({
        ...prev,
        page,
      }));
    }
  };

  return (
    <main className="min-h-screen overflow-hidden">

      {/* =====================================================
          HERO
          ===================================================== */}
      <section className="hero-panel relative isolate mx-3 overflow-hidden rounded-[2.25rem] bg-[#07150F] shadow-[0_50px_100px_-40px_rgba(7,21,15,0.7)] sm:mx-5 sm:rounded-[3rem]">

        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-emerald-500/15 blur-3xl" />

        <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-lime-500/10 blur-3xl" />

        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-full -translate-x-1/2 bg-gradient-to-l from-transparent via-white/20 to-transparent" />

        <div className="container-page relative py-20 sm:py-24 lg:py-28">

          <div className="max-w-3xl">

            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-xs font-black text-emerald-300 backdrop-blur">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400 shadow-lg shadow-emerald-400/50" />

              شبکه باشگاه‌های فوتبال شهرستان پاوه
            </span>

            <h1 className="mt-6 text-4xl font-black leading-[1.15] tracking-tight text-white sm:text-5xl lg:text-6xl">
              باشگاه‌های

              <span className="mt-1 block bg-gradient-to-l from-emerald-300 via-emerald-200 to-lime-300 bg-clip-text text-transparent">
                هیئت فوتبال شهرستان پاوه
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-sm leading-8 text-white/55 sm:text-base">
              باشگاه‌های عضو سامانه را کاوش کنید، اطلاعات رسمی هر باشگاه را
              مشاهده کنید و با بازیکنان، ترکیب تیم و افتخارات آن‌ ها بیشتر
              آشنا شوید.
            </p>

          </div>

          <div className="mt-10 grid max-w-4xl grid-cols-2 gap-3 sm:grid-cols-3">

            <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.06] p-4 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-400/10 text-emerald-300">
                  <Icon
                    name="shield"
                    className="h-5 w-5"
                  />
                </div>

                <div>
                  <p className="text-[10px] text-white/40">
                    باشگاه ثبت‌ شده
                  </p>

                  <strong className="mt-1 block text-2xl font-black text-white">
                    {totalLabel}
                  </strong>
                </div>
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.06] p-4 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-lime-400/10 text-lime-200">
                  <Icon
                    name="activity"
                    className="h-5 w-5"
                  />
                </div>

                <div>
                  <p className="text-[10px] text-white/40">
                    وضعیت سامانه
                  </p>

                  <strong className="mt-1 flex items-center gap-2 text-xs font-black text-emerald-300">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                    فعال و آنلاین
                  </strong>
                </div>
              </div>
            </div>

            <div className="hidden rounded-[1.5rem] border border-white/10 bg-white/[0.06] p-4 backdrop-blur-xl sm:block">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/10 text-white/80">
                  <Icon
                    name="eye"
                    className="h-5 w-5"
                  />
                </div>

                <div>
                  <p className="text-[10px] text-white/40">
                    دسترسی اطلاعات
                  </p>

                  <strong className="mt-1 block text-xs font-black text-white">
                    عمومی و رسمی
                  </strong>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =====================================================
          CONTENT
          ===================================================== */}
      <section className="container-page relative py-12 sm:py-16">

        {/* Search panel */}
        <div className="relative z-10 -mt-24 mb-10 rounded-[2rem] border border-slate-200/80 bg-white/95 p-4 shadow-2xl shadow-slate-900/[0.12] backdrop-blur-xl sm:p-5">

          <div className="flex flex-col gap-4 md:flex-row md:items-center">

            <div className="relative flex-1">
              <Input
                value={search}
                onChange={handleSearch}
                placeholder="نام باشگاه موردنظر را جستجو کنید..."
                className="h-13 pr-12"
              />

              <Icon
                name="search"
                className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              />
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-400 md:block">
                {loading
                  ? "در حال دریافت اطلاعات..."
                  : `${totalLabel} باشگاه`}
              </div>

              {search && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl bg-red-50 px-5 text-sm font-black text-red-500 transition hover:bg-red-100"
                >
                  <Icon
                    name="close"
                    className="h-4 w-4"
                  />

                  پاک کردن
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Section heading */}
        {!loading && clubs.length > 0 && (
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">

            <div>
              <span className="text-xs font-black text-emerald-600">
                فهرست باشگاه‌ها
              </span>

              <h2 className="mt-2 text-2xl font-black text-[#0B1F17] sm:text-3xl">
                {search
                  ? `نتایج جستجو برای «${search}»`
                  : "باشگاه‌های عضو سامانه"}
              </h2>

              <p className="mt-2 text-xs leading-6 text-slate-400 sm:text-sm">
                برای مشاهده ترکیب، بازیکنان و اطلاعات کامل هر باشگاه روی آن کلیک کنید.
              </p>
            </div>

            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-500 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              {clubs.length.toLocaleString("fa-IR")} مورد در این صفحه
            </div>

          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="h-40 animate-pulse rounded-[2rem] bg-white shadow-sm"
              />
            ))}
          </div>
        ) : clubs.length === 0 ? (
          <div className="rounded-[2.25rem] border border-dashed border-slate-200 bg-white px-6 py-20 text-center shadow-sm">

            <div className="mx-auto grid h-20 w-20 place-items-center rounded-[1.75rem] bg-slate-50 text-slate-300">
              <Icon
                name="shield"
                className="h-10 w-10"
              />
            </div>

            <h2 className="mt-6 text-xl font-black text-slate-700">
              باشگاهی پیدا نشد
            </h2>

            <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-slate-400">
              {search
                ? `باشگاهی با عبارت «${search}» در سامانه پیدا نشد.`
                : "هنوز باشگاهی برای نمایش در سامانه ثبت نشده است."}
            </p>

            {search && (
              <button
                type="button"
                onClick={clearSearch}
                className="mt-6 rounded-xl bg-emerald-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700"
              >
                نمایش همه باشگاه‌ها
              </button>
            )}

          </div>
        ) : (
          <>
            {/* Vertical / premium club cards */}
            <div className="space-y-4">
              {clubs.map((club) => (
                <ClubCard
                  key={club.id}
                  club={club}
                  onClick={() =>
                    navigate(`/clubs/${club.id}`)
                  }
                />
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div className="mt-12">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}

      </section>
    </main>
  );
}